import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle } from 'lucide-react';
import { slugify, formatPrice, bikePrice } from '../../utils';

// Shared by every series page: the buttons under the hero and the grid of models right after it.
const WHATSAPP = '18297163555';

export function bikeWhatsApp(bike) {
    const message = `Hola PROO MTB, quiero información de la Raymon ${bike.modelo}: precio, tallas y disponibilidad.`;
    return `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(message)}`;
}

const priceLabel = (bike) => (bikePrice(bike) ? formatPrice(bike.precio_eur, 'bikes') : 'Precio a consultar');

// Two calls to action for the model shown in the hero
export const HeroCtas = ({ bike }) => {
    if (!bike) return null;
    return (
        <div className="flex flex-wrap items-center justify-center gap-3 mt-6 pointer-events-auto">
            <Link
                to={`/product/${slugify(bike.modelo)}`}
                onClick={() => window.scrollTo(0, 0)}
                className="inline-flex items-center gap-2 bg-black text-white px-7 py-3.5 rounded-full text-xs font-black uppercase tracking-widest hover:bg-gray-800 transition-colors shadow-lg"
            >
                Ver ficha y tallas <ArrowRight size={16} />
            </Link>
            <a
                href={bikeWhatsApp(bike)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-black border border-black/15 px-7 py-3.5 rounded-full text-xs font-black uppercase tracking-widest hover:bg-black hover:text-white transition-colors shadow-lg"
            >
                <MessageCircle size={16} className="fill-current" /> Consultar por WhatsApp
            </a>
        </div>
    );
};

// All the models of the series, right under the hero, each with its own buttons
export const ModelsGrid = ({ bikes, name, id = 'modelos' }) => (
    <section id={id} className="max-w-[1400px] mx-auto px-4 py-16 md:py-24" aria-labelledby={`${id}-title`}>
        <div className="text-center mb-10 md:mb-14">
            <h2 id={`${id}-title`} className="text-3xl md:text-4xl font-black italic uppercase tracking-tight">
                Elige tu <span className="gradient-text-dark">{name}</span>
            </h2>
            <p className="text-gray-500 mt-3">
                {bikes.length} {bikes.length === 1 ? 'modelo' : 'modelos'}, de menor a mayor precio. Cada ficha trae las especificaciones y la tabla de tallas.
            </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {bikes.length > 0 ? bikes.map((bike) => (
                <article key={slugify(bike.modelo)} className="flex flex-col text-center">
                    <Link to={`/product/${slugify(bike.modelo)}`} className="group block" onClick={() => window.scrollTo(0, 0)}>
                        <div className="bg-gray-50 rounded-3xl p-8 mb-5 aspect-[4/3] flex items-center justify-center transition-colors group-hover:bg-gray-100">
                            <img
                                loading="lazy"
                                decoding="async"
                                src={bike.imagenes_urls && bike.imagenes_urls[0]}
                                alt={`Raymon ${bike.modelo}`}
                                className="max-w-full max-h-full object-contain mix-blend-multiply group-hover:scale-105 transition-transform duration-500"
                            />
                        </div>
                        <h3 className="text-xl font-black italic uppercase tracking-tight">{bike.modelo}</h3>
                        <p className="text-gray-500 font-medium text-lg mt-1">{priceLabel(bike)}</p>
                    </Link>
                    <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                        <Link
                            to={`/product/${slugify(bike.modelo)}`}
                            onClick={() => window.scrollTo(0, 0)}
                            className="inline-flex items-center gap-1.5 bg-black text-white px-5 py-2.5 rounded-full text-[11px] font-black uppercase tracking-wider hover:bg-gray-800 transition-colors"
                        >
                            Ver ficha <ArrowRight size={14} />
                        </Link>
                        <a
                            href={bikeWhatsApp(bike)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 border border-gray-200 text-black px-5 py-2.5 rounded-full text-[11px] font-black uppercase tracking-wider hover:border-black transition-colors"
                        >
                            <MessageCircle size={14} className="fill-current" /> Consultar
                        </a>
                    </div>
                </article>
            )) : (
                <div className="col-span-full text-center text-gray-400">Próximamente más modelos {name}.</div>
            )}
        </div>
    </section>
);
